import { ixAddToCart } from '@theme/ix-cart';

/**
 * <ix-ig-post> — Instagram-style product post.
 * Ported from the Interactive theme's ig-post-carousel section.
 *
 * - Scroll-snap product carousel with synced counter, dots and info bar.
 * - Info bar add button: adds directly when the product has a single
 *   available variant, otherwise opens a bottom sheet with option pills
 *   built from the product's /products/{handle}.js payload.
 * - Cart updates are announced with Shopify's CartLinesUpdateEvent so the
 *   Horizon cart drawer and cart bubble stay in sync.
 */
class IxIgPost extends HTMLElement {
  #product = null;
  #selected = {};
  #qty = 1;

  connectedCallback() {
    this.track = this.querySelector('.ig-post__track');
    this.slides = Array.from(this.querySelectorAll('.ig-post__slide'));
    this.dots = Array.from(this.querySelectorAll('.ig-post__dot'));
    this.counter = this.querySelector('.ig-post__counter [data-cur]');
    this.infoBar = this.querySelector('.ig-post__info-bar');
    this.sheet = this.querySelector('.ig-post__sheet');
    this.overlay = this.querySelector('.ig-post__sheet-overlay');
    this.sheetGrid = this.querySelector('.ig-post__sheet-grid');

    this.track?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const slide = this.slides[i];
        if (slide) this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      });
    });

    this.querySelector('.ig-post__add')?.addEventListener('click', () => this.#onAddClick());
    this.querySelector('.ig-post__sheet-close')?.addEventListener('click', () => this.#closeSheet());
    this.overlay?.addEventListener('click', () => this.#closeSheet());

    this.#syncToSlide(0);
    this.#setupAutoplay();
  }

  #timer = null;

  #setupAutoplay() {
    const seconds = Number(this.dataset.autoplay || 0);
    if (!seconds || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const advance = () => {
      const next = (this.#currentIndex() + 1) % this.slides.length;
      this.track.scrollTo({ left: this.slides[next]?.offsetLeft || 0, behavior: 'smooth' });
    };
    const stop = () => clearInterval(this.#timer);

    // A manual swipe permanently stops the autoplay, like the original.
    this.track.addEventListener('pointerdown', stop, { once: true });

    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        clearInterval(this.#timer);
        if (entry.isIntersecting) this.#timer = setInterval(advance, seconds * 1000);
      });
    }).observe(this);
  }

  #pending = false;

  #onScroll = () => {
    if (this.#pending) return;
    this.#pending = true;
    requestAnimationFrame(() => {
      this.#pending = false;
      this.#syncToSlide(this.#currentIndex());
    });
  };

  #currentIndex() {
    let nearest = 0;
    let nearestDistance = Infinity;
    this.slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - this.track.scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = i;
      }
    });
    return nearest;
  }

  #syncToSlide(index) {
    const slide = this.slides[index];
    if (!slide) return;

    if (this.counter) this.counter.textContent = String(index + 1);
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

    if (!this.infoBar) return;
    const { variant, priceText, compareText, vendor, title } = slide.dataset;
    if (!variant) {
      this.infoBar.setAttribute('aria-hidden', 'true');
      return;
    }
    this.infoBar.setAttribute('aria-hidden', 'false');
    this.infoBar.querySelector('[data-price]').textContent = priceText || '';
    this.infoBar.querySelector('[data-compare]').textContent = compareText || '';
    this.infoBar.querySelector('[data-vendor]').textContent = vendor || '';
    this.infoBar.querySelector('[data-title]').textContent = title || '';
    const opts = this.infoBar.querySelector('[data-opts]');
    if (opts) {
      const optionCount = Number(slide.dataset.opts || 0);
      opts.textContent = optionCount > 1 ? `${optionCount} options` : '';
    }
  }

  async #onAddClick() {
    const slide = this.slides[this.#currentIndex()];
    if (!slide?.dataset.variant) return;

    if (slide.dataset.available === 'false') {
      if (slide.dataset.url) window.location.href = slide.dataset.url;
      return;
    }

    const optionCount = Number(slide.dataset.opts || 0);
    if (optionCount <= 1) {
      this.#addToCart(slide.dataset.variant, 1);
      return;
    }

    try {
      const response = await fetch(`/products/${slide.dataset.handle}.js`);
      this.#product = await response.json();
      this.#openSheet();
    } catch {
      if (slide.dataset.url) window.location.href = slide.dataset.url;
    }
  }

  #openSheet() {
    if (!this.#product || !this.sheet) return;
    this.#selected = {};
    this.#qty = 1;
    this.#renderSheet();
    this.sheet.hidden = false;
    this.overlay.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    document.body.style.overflow = 'hidden';
  }

  #closeSheet() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(() => {
      if (this.sheet) this.sheet.hidden = true;
      if (this.overlay) this.overlay.hidden = true;
    }, 280);
  }

  #renderSheet() {
    const product = this.#product;
    this.sheetGrid.innerHTML = '';

    product.options.forEach((option, optionIndex) => {
      // /products/{handle}.js returns options as strings on some stores and
      // as {name, position, values} objects on others
      const optionName = typeof option === 'string' ? option : option?.name || '';
      const values = [...new Set(product.variants.map((v) => v.options[optionIndex]))];
      if (this.#selected[optionIndex] == null) this.#selected[optionIndex] = values[0];

      const wrap = document.createElement('div');
      wrap.className = 'ig-post__opt';
      const title = document.createElement('div');
      title.className = 'ig-post__opt-title';
      title.textContent = optionName;
      wrap.appendChild(title);

      const pills = document.createElement('div');
      pills.className = 'ig-post__pills';
      values.forEach((value) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'ig-post__pill';
        pill.textContent = value;
        pill.setAttribute('aria-pressed', this.#selected[optionIndex] === value ? 'true' : 'false');
        const anyAvailable = product.variants.some((v) => v.options[optionIndex] === value && v.available);
        if (!anyAvailable) pill.dataset.disabled = '1';
        pill.addEventListener('click', () => {
          this.#selected[optionIndex] = value;
          this.#renderSheet();
        });
        pills.appendChild(pill);
      });
      wrap.appendChild(pills);
      this.sheetGrid.appendChild(wrap);
    });

    const variant = this.#resolveVariant();

    const footer = document.createElement('div');
    footer.className = 'ig-post__sheet-footer';

    const qty = document.createElement('div');
    qty.className = 'ig-post__qty';
    qty.innerHTML = `
      <button type="button" class="ig-post__qty-btn" data-step="-1" aria-label="−">−</button>
      <span class="ig-post__qty-value">${this.#qty}</span>
      <button type="button" class="ig-post__qty-btn" data-step="1" aria-label="+">+</button>`;
    qty.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', () => {
        this.#qty = Math.max(1, this.#qty + Number(button.dataset.step));
        qty.querySelector('.ig-post__qty-value').textContent = String(this.#qty);
      });
    });

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'ig-post__atc';
    add.textContent = variant?.available
      ? `Add to cart — ${this.#formatMoney(variant.price)}`
      : 'Sold out';
    add.disabled = !variant?.available;
    add.addEventListener('click', async () => {
      if (!variant) return;
      add.disabled = true;
      await this.#addToCart(variant.id, this.#qty);
      this.#closeSheet();
    });

    footer.appendChild(qty);
    footer.appendChild(add);
    this.sheetGrid.appendChild(footer);
  }

  #resolveVariant() {
    return this.#product?.variants.find((variant) =>
      variant.options.every((value, i) => value === this.#selected[i])
    );
  }

  #formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || '';
    return `${(cents / 100).toFixed(2)} ${currency}`.trim();
  }

  async #addToCart(variantId, quantity) {
    try {
      await ixAddToCart(variantId, quantity, 'ix-ig-post');
    } catch {
      // The deferred cart event already carries the failure.
    }
  }
}

if (!customElements.get('ix-ig-post')) {
  customElements.define('ix-ig-post', IxIgPost);
}
